import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import { useTranslation } from 'react-i18next';

import { createSignInSchema, type SignInFormValues } from '../schemas';
import { useLogin } from '../hooks/useLogin';
import { SocialAuthGroup } from '../components/SocialAuthGroup';
import { AuthDivider } from '../components/AuthDivider';
import { ToggleSwitch } from '@/shared/components/custom/toggle-switch';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';

import { Button } from '@/shared/components/ui/button';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { FieldGroup } from '@/shared/components/ui/field';

export function LoginPage() {
  const { t } = useTranslation('auth');
  const { loginWithPassword, isPending, isError, error } = useLogin();
  const [showPassword, setShowPassword] = useState(false);

  const schema = createSignInSchema(t);

  const form = useForm<SignInFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', remember: false },
  });

  const onSubmit = async (values: SignInFormValues) => {
    await loginWithPassword(values);
  };

  const passwordToggle = (
    <button
      type="button"
      onClick={() => setShowPassword((v) => !v)}
      aria-label={showPassword ? t('aria.hide_password') : t('aria.show_password')}
      data-testid="auth-login-password-toggle"
      className="text-muted-foreground hover:text-foreground transition-colors"
    >
      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  );

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <Card className="w-full border-0 shadow-none bg-transparent">
        <CardHeader className="text-center space-y-1">
          <CardTitle className="text-2xl font-bold">{t('login.title')}</CardTitle>
          <CardDescription>{t('login.subtitle')}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4 px-0">
          <SocialAuthGroup />

          <AuthDivider label={t('login.divider')} />

          <form id="login-form" onSubmit={form.handleSubmit(onSubmit)}>
            <FieldGroup>
              {isError && (
                <Alert variant="destructive" data-testid="auth-login-error-alert">
                  <AlertDescription>{error?.message ?? t('errors.generic')}</AlertDescription>
                </Alert>
              )}

              <FormFieldInput
                control={form.control}
                name="email"
                label={t('login.email')}
                type="email"
                autoComplete="email"
                disabled={isPending}
                floatingLabel
                floatingVariant="default"
                data-testid="auth-login-email-input"
              />

              <FormFieldInput
                control={form.control}
                name="password"
                label={t('login.password')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                disabled={isPending}
                floatingLabel
                floatingVariant="default"
                endElement={passwordToggle}
                data-testid="auth-login-password-input"
              />
            </FieldGroup>
          </form>

          {/* Remember + Forgot */}
          <div className="flex items-center justify-between">
            <Controller
              name="remember"
              control={form.control}
              render={({ field }) => (
                <ToggleSwitch
                  id="remember"
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                  label={t('login.remember')}
                  disabled={isPending}
                  data-testid="auth-login-remember-toggle"
                />
              )}
            />
            <Link
              to="/forgot-password"
              className="text-sm text-primary hover:underline underline-offset-2 transition-colors"
              data-testid="auth-login-forgot-link"
            >
              {t('login.forgot')}
            </Link>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            form="login-form"
            className="w-full"
            disabled={isPending}
            variant="default"
            aria-busy={isPending}
            data-testid="auth-login-submit"
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                {t('login.submitting')}
              </>
            ) : (
              <>
                {t('login.submit')}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>

          <p className="text-sm text-muted-foreground text-center">
            {t('login.no_account')}{' '}
            <Link
              to="/register"
              className="text-primary hover:underline underline-offset-2 transition-colors"
              data-testid="auth-login-register-link"
            >
              {t('login.register_link')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </FeatureErrorBoundary>
  );
}
